import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { FileText, Trash2, AlertTriangle, Calendar, IndianRupee, Download, FileSpreadsheet, Pencil, CheckCircle2, AlertCircle, Clock, Send, XCircle, CreditCard, Search, History, Eye, MoreHorizontal, Filter, ExternalLink, Percent } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDate } from '@/utils/dateFormat';

const GST_BRACKETS = [
  { value: '0', label: '0% (Exempt)' },
  { value: '5', label: '5% GST' },
  { value: '12', label: '12% GST' },
  { value: '18', label: '18% GST' },
  { value: '28', label: '28% GST' },
];

// Invoice status definitions
const INVOICE_STATUSES = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600', icon: Clock },
  in_review: { label: 'In Review', color: 'bg-purple-100 text-purple-600', icon: Search },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-600', icon: CheckCircle2 },
  amended: { label: 'Amended', color: 'bg-amber-100 text-amber-600', icon: History },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-600', icon: XCircle },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-600', icon: Send },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-600', icon: IndianRupee },
  unpaid: { label: 'Unpaid', color: 'bg-orange-100 text-orange-600', icon: CreditCard },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-600', icon: AlertCircle },
};

// Valid status transitions
const STATUS_TRANSITIONS = {
  draft: ['in_review', 'approved', 'amended', 'rejected'],
  in_review: ['approved', 'rejected', 'amended', 'draft'],
  approved: ['sent', 'amended', 'rejected', 'in_review'],
  amended: ['approved', 'in_review', 'rejected'],
  rejected: ['draft', 'in_review', 'amended'],
  sent: ['paid', 'unpaid', 'overdue', 'amended'],
  paid: ['unpaid', 'amended'],
  unpaid: ['paid', 'overdue', 'amended', 'approved'],
  overdue: ['paid', 'unpaid', 'amended'],
};

export default function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editGstDialogOpen, setEditGstDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [selectedGst, setSelectedGst] = useState('18');
  const [newStatus, setNewStatus] = useState('');
  const [amendmentReason, setAmendmentReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountTarget, setDiscountTarget] = useState(null);
  const [discountEditValue, setDiscountEditValue] = useState(0);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
      ]);
      setInvoices(invoicesRes.data);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const getClientName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    if (project?.client_id) {
      const client = users.find((u) => u.id === project.client_id);
      return client?.name || 'Unknown Client';
    }
    return 'No Client';
  };

  const getUserName = (userId) => {
    const foundUser = users.find((u) => u.id === userId);
    return foundUser?.name || 'Unknown User';
  };

  const getStatusConfig = (status) => {
    return INVOICE_STATUSES[status] || INVOICE_STATUSES.draft;
  };

  const getAvailableTransitions = (status) => {
    return STATUS_TRANSITIONS[status] || [];
  };

  const handleDeleteClick = (invoice) => {
    setSelectedInvoice(invoice);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteReason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    try {
      await axios.delete(`${API}/invoices/${selectedInvoice.id}`, {
        data: { reason: deleteReason }
      });
      toast.success(`Invoice ${selectedInvoice.invoice_number} deleted successfully`);
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
      setDeleteReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleEditGstClick = (invoice) => {
    setSelectedInvoice(invoice);
    setSelectedGst(String(invoice.tax_percentage));
    setEditGstDialogOpen(true);
  };

  const handleGstUpdate = async () => {
    try {
      const newTaxPercentage = parseFloat(selectedGst);
      const subtotal = selectedInvoice.subtotal;
      const newTaxAmount = (subtotal * newTaxPercentage) / 100;
      const discountAmount = selectedInvoice.discount_amount || 0;
      const newTotal = subtotal + newTaxAmount - discountAmount;

      await axios.put(`${API}/invoices/${selectedInvoice.id}`, {
        tax_percentage: newTaxPercentage,
        tax_amount: newTaxAmount,
        total: newTotal
      });

      toast.success('GST updated successfully');
      setEditGstDialogOpen(false);
      setSelectedInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update GST');
    }
  };

  const handleStatusClick = (invoice, status) => {
    setSelectedInvoice(invoice);
    setNewStatus(status);
    setAmendmentReason('');
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (newStatus === 'amended' && !amendmentReason.trim()) {
      toast.error('Please provide a reason for amendment');
      return;
    }

    try {
      const payload = { status: newStatus };
      if (newStatus === 'amended') {
        payload.amendment_reason = amendmentReason;
      }

      await axios.put(`${API}/invoices/${selectedInvoice.id}`, payload);

      const statusLabel = INVOICE_STATUSES[newStatus]?.label || newStatus;
      toast.success(`Invoice status changed to ${statusLabel}`);
      setStatusDialogOpen(false);
      setSelectedInvoice(null);
      setNewStatus('');
      setAmendmentReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleExport = async (invoiceId, format) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/export/${format}`, {
        responseType: 'blob'
      });
      
      const invoice = invoices.find(inv => inv.id === invoiceId);
      const filename = `${invoice?.invoice_number || 'Invoice'}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${filename} successfully`);
    } catch (error) {
      toast.error('Failed to export invoice');
    }
  };

  // Preview PDF in new tab
  const handlePreviewPDF = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/export/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to preview PDF');
    }
  };

  const handleViewDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailDialogOpen(true);
  };

  const getTotalInvoiced = () => {
    return invoices.reduce((sum, inv) => sum + inv.total, 0);
  };

  const getPaidAmount = () => {
    return invoices.filter((inv) => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0);
  };

  const getPendingAmount = () => {
    return invoices.filter((inv) => inv.status !== 'paid').reduce((sum, inv) => sum + inv.total, 0);
  };

  // Update discount on invoice
  const handleDiscountSave = async () => {
    if (!discountTarget) return;
    try {
      const subtotal = discountTarget.subtotal || 0;
      const disc = parseFloat(discountEditValue) || 0;
      const discountAmount = Math.round(subtotal * disc / 100 * 100) / 100;
      const newTotal = subtotal + (discountTarget.tax_amount || 0) - discountAmount;
      await axios.put(`${API}/invoices/${discountTarget.id}`, {
        discount_percentage: disc,
        discount_amount: discountAmount,
        total: newTotal
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Discount updated');
      setDiscountDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update discount');
    }
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(invoice => {
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    const matchesSearch = searchTerm === '' || 
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getProjectName(invoice.project_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
      getClientName(invoice.project_id).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-5xl font-bold tracking-tight text-stone-900 mb-2" data-testid="invoices-title">
              Invoices
            </h1>
            <p className="text-stone-500">Manage invoices generated from BOQ worksheets</p>
          </div>
          <Button onClick={() => navigate('/boq-worksheets')} className="apple-button">
            Go to BOQ Worksheets
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="apple-card p-4" data-testid="total-invoiced">
            <div className="flex items-center gap-3 mb-2">
              <FileText size={20} className="text-stone-800" />
              <span className="text-2xl font-bold">{invoices.length}</span>
            </div>
            <p className="text-sm text-stone-500">Total Invoices</p>
            <p className="text-lg font-semibold text-stone-900 mt-1">₹{getTotalInvoiced().toLocaleString()}</p>
          </div>

          <div className="apple-card p-4" data-testid="paid-count">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 size={20} className="text-emerald-500" />
              <span className="text-2xl font-bold text-emerald-600">{invoices.filter(inv => inv.status === 'paid').length}</span>
            </div>
            <p className="text-sm text-stone-500">Paid</p>
            <p className="text-lg font-semibold text-emerald-600 mt-1">₹{getPaidAmount().toLocaleString()}</p>
          </div>

          <div className="apple-card p-4" data-testid="pending-count">
            <div className="flex items-center gap-3 mb-2">
              <Clock size={20} className="text-orange-500" />
              <span className="text-2xl font-bold text-orange-600">{invoices.filter(inv => inv.status !== 'paid').length}</span>
            </div>
            <p className="text-sm text-stone-500">Pending</p>
            <p className="text-lg font-semibold text-orange-600 mt-1">₹{getPendingAmount().toLocaleString()}</p>
          </div>

          <div className="apple-card p-4" data-testid="overdue-count">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle size={20} className="text-red-500" />
              <span className="text-2xl font-bold text-red-600">{invoices.filter(inv => inv.status === 'overdue').length}</span>
            </div>
            <p className="text-sm text-stone-500">Overdue</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <Input
              placeholder="Search by invoice #, project, or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
              data-testid="search-invoices"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
              <Filter size={16} className="mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(INVOICE_STATUSES).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Invoices Table */}
        {filteredInvoices.length === 0 ? (
          <div className="apple-card p-16 text-center">
            <FileText size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500 text-lg">
              {invoices.length === 0 ? 'No invoices generated yet' : 'No invoices match your filters'}
            </p>
            {invoices.length === 0 && (
              <p className="text-stone-400 text-sm mt-2">
                Convert BOQ worksheets to invoices from the BOQ Worksheets page
              </p>
            )}
          </div>
        ) : (
          <div className="apple-card overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead className="font-semibold">Invoice #</TableHead>
                    <TableHead className="font-semibold">Project</TableHead>
                    <TableHead className="font-semibold">Client</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Due Date</TableHead>
                    <TableHead className="font-semibold text-right">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => {
                    const statusConfig = getStatusConfig(invoice.status);
                    const StatusIcon = statusConfig.icon;
                    const availableTransitions = getAvailableTransitions(invoice.status);
                    
                    return (
                      <TableRow key={invoice.id} className="group hover:bg-stone-50" data-testid={`invoice-row-${invoice.id}`}>
                        <TableCell className="font-semibold text-stone-900">
                          {invoice.invoice_number}
                        </TableCell>
                        <TableCell>
                          <button 
                            onClick={() => navigate(`/projects/${invoice.project_id}`)}
                            className="text-stone-800 hover:underline text-left"
                          >
                            {getProjectName(invoice.project_id)}
                          </button>
                        </TableCell>
                        <TableCell className="text-stone-600">
                          {getClientName(invoice.project_id)}
                        </TableCell>
                        <TableCell className="text-stone-600">
                          {formatDate(invoice.created_at)}
                        </TableCell>
                        <TableCell className="text-stone-600">
                          {formatDate(invoice.due_date)}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-stone-900">
                          ₹{invoice.total.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${statusConfig.color}`}>
                            <StatusIcon size={12} />
                            {statusConfig.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {/* View Details */}
                            <button
                              onClick={() => handleViewDetails(invoice)}
                              className="p-1.5 text-stone-500 hover:text-stone-800 hover:bg-primary/10 rounded-lg transition-colors"
                              title="View Details"
                              data-testid={`view-invoice-${invoice.id}`}
                            >
                              <Eye size={16} />
                            </button>

                            {/* Preview PDF in new tab */}
                            <button
                              onClick={() => handlePreviewPDF(invoice.id)}
                              className="p-1.5 text-stone-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Preview PDF"
                              data-testid={`preview-pdf-${invoice.id}`}
                            >
                              <ExternalLink size={16} />
                            </button>
                            
                            {(user.role === 'owner' || user.role === 'staff') && (
                              <>
                                {/* Status Change */}
                                {availableTransitions.length > 0 && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button
                                        className="p-1.5 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Change Status"
                                        data-testid={`change-status-${invoice.id}`}
                                      >
                                        <StatusIcon size={16} />
                                      </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <div className="px-2 py-1.5 text-xs font-medium text-stone-500">Change Status</div>
                                      <DropdownMenuSeparator />
                                      {availableTransitions.map((status) => {
                                        const config = INVOICE_STATUSES[status];
                                        const Icon = config.icon;
                                        return (
                                          <DropdownMenuItem key={status} onClick={() => handleStatusClick(invoice, status)}>
                                            <Icon size={14} className="mr-2" />
                                            {config.label}
                                          </DropdownMenuItem>
                                        );
                                      })}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}

                                {/* Export */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      className="p-1.5 text-stone-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title="Export"
                                      data-testid={`export-invoice-${invoice.id}`}
                                    >
                                      <Download size={16} />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleExport(invoice.id, 'excel')}>
                                      <FileSpreadsheet size={14} className="mr-2 text-green-600" />
                                      Export to Excel
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleExport(invoice.id, 'pdf')}>
                                      <FileText size={14} className="mr-2 text-red-600" />
                                      Export to PDF
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>

                                {/* Edit GST */}
                                {invoice.status !== 'paid' && (
                                  <button
                                    onClick={() => handleEditGstClick(invoice)}
                                    className="p-1.5 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Edit GST"
                                    data-testid={`edit-gst-${invoice.id}`}
                                  >
                                    <Pencil size={16} />
                                  </button>
                                )}

                                {/* Edit Discount */}
                                {invoice.status !== 'paid' && (
                                  <button
                                    onClick={() => { setDiscountTarget(invoice); setDiscountEditValue(invoice.discount_percentage || 0); setDiscountDialogOpen(true); }}
                                    className="p-1.5 text-stone-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                    title="Edit Discount"
                                    data-testid={`edit-discount-invoice-${invoice.id}`}
                                  >
                                    <Percent size={16} />
                                  </button>
                                )}

                                {/* Delete */}
                                {invoice.status !== 'paid' && (
                                  <button
                                    onClick={() => handleDeleteClick(invoice)}
                                    className="p-1.5 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete"
                                    data-testid={`delete-invoice-${invoice.id}`}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile List */}
            <div className="md:hidden divide-y divide-stone-100">
              {filteredInvoices.map((invoice) => {
                const statusConfig = getStatusConfig(invoice.status);
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div 
                    key={invoice.id} 
                    className="p-4 space-y-3"
                    data-testid={`invoice-card-${invoice.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-stone-900">{invoice.invoice_number}</p>
                        <p className="text-sm text-stone-500">{getProjectName(invoice.project_id)}</p>
                        <p className="text-xs text-stone-400">{getClientName(invoice.project_id)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-stone-900">₹{invoice.total.toLocaleString()}</p>
                        <span className={`mt-1 px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 ${statusConfig.color}`}>
                          <StatusIcon size={10} />
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-stone-500">
                      <span>Created: {formatDate(invoice.created_at)}</span>
                      <span>Due: {formatDate(invoice.due_date)}</span>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => handleViewDetails(invoice)}>
                        <Eye size={14} className="mr-1" /> View
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <MoreHorizontal size={14} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleExport(invoice.id, 'pdf')}>
                            <FileText size={14} className="mr-2" /> Export PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExport(invoice.id, 'excel')}>
                            <FileSpreadsheet size={14} className="mr-2" /> Export Excel
                          </DropdownMenuItem>
                          {invoice.status !== 'paid' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleEditGstClick(invoice)}>
                                <Pencil size={14} className="mr-2" /> Edit GST
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setDiscountTarget(invoice); setDiscountEditValue(invoice.discount_percentage || 0); setDiscountDialogOpen(true); }}>
                                <Percent size={14} className="mr-2" /> Edit Discount
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteClick(invoice)} className="text-red-600">
                                <Trash2 size={14} className="mr-2" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Invoice Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-dialog">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-xl flex items-center gap-3">
                {selectedInvoice?.invoice_number}
                {selectedInvoice && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${getStatusConfig(selectedInvoice.status).color}`}>
                    {getStatusConfig(selectedInvoice.status).label}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-6 py-4">
                {/* Invoice Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-stone-50 rounded-xl">
                  <div>
                    <p className="text-xs text-stone-500">Project</p>
                    <p className="font-medium text-stone-900">{getProjectName(selectedInvoice.project_id)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Client</p>
                    <p className="font-medium text-stone-900">{getClientName(selectedInvoice.project_id)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Created</p>
                    <p className="font-medium text-stone-900">{formatDate(selectedInvoice.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Due Date</p>
                    <p className="font-medium text-stone-900">{formatDate(selectedInvoice.due_date)}</p>
                  </div>
                </div>

                {/* Line Items Table */}
                <div className="border border-stone-200 rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-stone-50">
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedInvoice.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <p className="font-medium text-stone-900">{item.task_title}</p>
                            {item.description && <p className="text-xs text-stone-500">{item.description}</p>}
                          </TableCell>
                          <TableCell className="text-right text-stone-600">{item.quantity} {item.unit}</TableCell>
                          <TableCell className="text-right text-stone-600">₹{item.rate.toLocaleString()}</TableCell>
                          <TableCell className="text-right font-semibold text-stone-900">₹{item.amount.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Totals */}
                <div className="space-y-2 p-4 bg-stone-50 rounded-xl">
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-500">Subtotal</span>
                    <span className="text-stone-900">₹{selectedInvoice.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-500">GST ({selectedInvoice.tax_percentage}%)</span>
                    <span className="text-stone-900">₹{selectedInvoice.tax_amount.toLocaleString()}</span>
                  </div>
                  {selectedInvoice.discount_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Discount ({selectedInvoice.discount_percentage}%)</span>
                      <span className="text-red-600">-₹{selectedInvoice.discount_amount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-stone-200 font-semibold">
                    <span className="text-stone-900">Total</span>
                    <span className="text-stone-900 text-lg">₹{selectedInvoice.total.toLocaleString()}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => handlePreviewPDF(selectedInvoice.id)} data-testid="preview-pdf-detail">
                    <ExternalLink size={16} className="mr-2" /> Preview PDF
                  </Button>
                  <Button variant="outline" onClick={() => handleExport(selectedInvoice.id, 'pdf')}>
                    <FileText size={16} className="mr-2" /> Export PDF
                  </Button>
                  <Button variant="outline" onClick={() => handleExport(selectedInvoice.id, 'excel')}>
                    <FileSpreadsheet size={16} className="mr-2" /> Export Excel
                  </Button>
                  <Button onClick={() => navigate(`/projects/${selectedInvoice.project_id}`)} className="apple-button">
                    View Project
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-invoice-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-stone-900">
                <AlertTriangle className="text-red-500" size={20} />
                Delete Invoice
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p className="text-stone-600">
                    Are you sure you want to delete invoice <strong className="text-stone-900">{selectedInvoice?.invoice_number}</strong>?
                    This action cannot be undone.
                  </p>
                  <div>
                    <Label htmlFor="delete-reason" className="text-stone-900 font-medium">
                      Reason for deletion <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="delete-reason"
                      placeholder="Please provide a reason for deleting this invoice..."
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      className="mt-2 bg-white text-stone-900 placeholder:text-stone-400 border-stone-300"
                      rows={3}
                      data-testid="delete-reason-input"
                    />
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="text-stone-900" onClick={() => setDeleteReason('')}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-invoice-button"
              >
                Delete Invoice
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit GST Dialog */}
        <Dialog open={editGstDialogOpen} onOpenChange={setEditGstDialogOpen}>
          <DialogContent data-testid="edit-gst-dialog">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-xl">Edit GST Rate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-stone-600">
                Change GST rate for invoice <strong className="text-stone-900">{selectedInvoice?.invoice_number}</strong>
              </p>
              
              <div>
                <Label className="text-stone-900 font-medium">GST Bracket</Label>
                <Select value={selectedGst} onValueChange={setSelectedGst}>
                  <SelectTrigger className="mt-2 bg-white text-stone-900 border-stone-300" data-testid="gst-select">
                    <SelectValue placeholder="Select GST rate" />
                  </SelectTrigger>
                  <SelectContent>
                    {GST_BRACKETS.map((bracket) => (
                      <SelectItem key={bracket.value} value={bracket.value}>
                        {bracket.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedInvoice && (
                <div className="p-4 bg-stone-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-500">Subtotal:</span>
                    <span className="text-stone-900">₹{selectedInvoice.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-500">GST ({selectedGst}%):</span>
                    <span className="text-stone-900">₹{((selectedInvoice.subtotal * parseFloat(selectedGst)) / 100).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-500">Discount:</span>
                    <span className="text-stone-900">-₹{(selectedInvoice.discount_amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="border-t border-stone-200 pt-2 flex justify-between font-semibold">
                    <span className="text-stone-900">New Total:</span>
                    <span className="text-stone-900">
                      ₹{(selectedInvoice.subtotal + (selectedInvoice.subtotal * parseFloat(selectedGst)) / 100 - (selectedInvoice.discount_amount || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setEditGstDialogOpen(false)} className="text-stone-900">
                Cancel
              </Button>
              <Button onClick={handleGstUpdate} className="apple-button" data-testid="save-gst-button">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Status Change Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent data-testid="status-change-dialog">
            <DialogHeader>
              <DialogTitle className="text-stone-900 font-manrope text-xl">
                Change Invoice Status
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-stone-600">
                Change status for invoice <strong className="text-stone-900">{selectedInvoice?.invoice_number}</strong> to{' '}
                <strong className="text-stone-900">{INVOICE_STATUSES[newStatus]?.label}</strong>
              </p>
              
              {newStatus === 'amended' && (
                <div>
                  <Label htmlFor="amendment-reason" className="text-stone-900 font-medium">
                    Amendment Reason <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="amendment-reason"
                    placeholder="Please explain why this invoice needs to be amended..."
                    value={amendmentReason}
                    onChange={(e) => setAmendmentReason(e.target.value)}
                    className="mt-2 bg-white text-stone-900 placeholder:text-stone-400 border-stone-300"
                    rows={3}
                    data-testid="amendment-reason-input"
                  />
                </div>
              )}

              {newStatus === 'approved' && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-green-600 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-green-800">Approve Invoice</p>
                      <p className="text-sm text-green-600">This invoice will be marked as approved and can be sent to the client.</p>
                    </div>
                  </div>
                </div>
              )}

              {newStatus === 'paid' && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <IndianRupee className="text-emerald-600 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-emerald-800">Mark as Paid</p>
                      <p className="text-sm text-emerald-600">This invoice will be marked as paid.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setStatusDialogOpen(false);
                  setAmendmentReason('');
                }} 
                className="text-stone-900"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleStatusUpdate} 
                className={`${newStatus === 'approved' ? 'bg-green-600 hover:bg-green-700' : newStatus === 'paid' ? 'bg-emerald-600 hover:bg-emerald-700' : 'apple-button'} text-white`}
                data-testid="confirm-status-button"
              >
                Confirm
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      {/* Discount Edit Dialog */}
      <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Invoice Discount</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Discount Percentage (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={discountEditValue}
                onChange={(e) => setDiscountEditValue(e.target.value)}
                data-testid="invoice-discount-edit-input"
              />
            </div>
            {discountTarget && (
              <div className="text-sm text-stone-500">
                <p>Subtotal: ₹{discountTarget.subtotal?.toLocaleString()}</p>
                <p>Tax: ₹{(discountTarget.tax_amount || 0).toLocaleString()}</p>
                <p>Discount: ₹{Math.round(discountTarget.subtotal * (parseFloat(discountEditValue) || 0) / 100).toLocaleString()}</p>
                <p className="font-medium text-stone-900">New Total: ₹{Math.round(discountTarget.subtotal + (discountTarget.tax_amount || 0) - discountTarget.subtotal * (parseFloat(discountEditValue) || 0) / 100).toLocaleString()}</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDiscountDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleDiscountSave} data-testid="save-invoice-discount-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}
